/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops ダッシュボード「内訳」タブのサービス別差戻しテーブル。期間内の依頼とそのステート履歴から、サービスごとに 差戻し率（差戻された依頼数 / 依頼数 を併記）・調査中 / 未対応に戻るまでの平均（土日除外、24 時間未満は時間・以上は SLA と同じ数え方の営業日）・再開せず完了・保留中・差戻し理由の内訳 を出す。集計は ops-core の aggregateOpsSendBacksByService。履歴を持つダッシュボード起票の依頼のみが対象で、理由は記録開始後の差戻しのみ
 * @graph-connects aggregateOpsSendBacksByService [calls] サービス別の差戻し統計
 * @graph-connects OpsFetchErrorPanel [references] 取得失敗の表示
 * @graph-connects OpsAggregatingPanel [references] 集計中の表示
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  OPS_SEND_BACK_REASONS,
  OPS_SEND_BACK_REASON_SINCE_JST_LABEL,
  OPS_SERVICE_LABELS,
  OPS_SERVICE_SLUGS,
  aggregateOpsSendBacksByService,
  type OpsStateHistoryEvent,
} from '@cortex/ops-core';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { OpsAggregatingPanel, OpsFetchErrorPanel } from './OpsDashboardStates.js';
import { formatOpsMetric, opsDurationParts } from '../../../lib/ops-metric-format.js';
import type { OpsRequest } from '../../../types/ops.js';

/** OpsSendBackBreakdownCard の props。 */
export interface OpsSendBackBreakdownCardProps {
  /** 期間内の依頼（サービス絞り込み前。サービス別に並べるため全サービスを渡す）。 */
  requests: OpsRequest[];
  /** 期間内 native 依頼のステート履歴（取得前は undefined）。 */
  events: OpsStateHistoryEvent[] | undefined;
  /** 見出しに出す期間ラベル。 */
  periodLabel: string;
  /** 履歴を取得中か。 */
  isLoading: boolean;
  /** 履歴の取得に失敗したか。 */
  isError: boolean;
  /** 再取得する。 */
  onRetry: () => void;
}

/**
 * サービス別差戻しテーブル。取得中・失敗は専用の表示に切り替える。
 * @graph-connects aggregateOpsSendBacksByService [calls] サービス別の差戻し統計
 */
export function OpsSendBackBreakdownCard({
  requests,
  events,
  periodLabel,
  isLoading,
  isError,
  onRetry,
}: OpsSendBackBreakdownCardProps): ReactNode {
  const { t } = useTranslation();
  const duration = (hours: number | null, businessDays: number | null): string => {
    if (hours === null || businessDays === null) return formatOpsMetric(null);
    const parts = opsDurationParts(hours, businessDays);
    return t(`ops.dashboard.bySendBack.duration.${parts.unit}`, {
      value: parts.value,
      count: parts.value,
    });
  };
  const header = (
    <div className="border-b border-[var(--color-border)] px-4 py-3">
      <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
        {t('ops.dashboard.bySendBack.title', { period: periodLabel })}
      </h2>
      <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
        {t('ops.dashboard.bySendBack.note', { since: OPS_SEND_BACK_REASON_SINCE_JST_LABEL })}
      </p>
    </div>
  );
  if (isError) {
    return (
      <Card padding="none">
        {header}
        <OpsFetchErrorPanel onRetry={onRetry} />
      </Card>
    );
  }
  if (isLoading || events === undefined) {
    return (
      <Card padding="none">
        {header}
        <OpsAggregatingPanel />
      </Card>
    );
  }
  const rows = aggregateOpsSendBacksByService(OPS_SERVICE_SLUGS, requests, events);
  return (
    <Card padding="none">
      {header}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="text-left text-xs text-[var(--color-text-muted)]">
              <th className="px-4 py-2 font-medium">
                {t('ops.dashboard.bySendBack.columns.service')}
              </th>
              <th className="px-2 py-2 font-medium">
                {t('ops.dashboard.bySendBack.columns.rate')}
              </th>
              <th className="px-2 py-2 font-medium">
                {t('ops.dashboard.bySendBack.columns.avgResume')}
              </th>
              <th className="px-2 py-2 font-medium">
                {t('ops.dashboard.bySendBack.columns.closedWithoutResume')}
              </th>
              <th className="px-2 py-2 font-medium">
                {t('ops.dashboard.bySendBack.columns.pending')}
              </th>
              <th className="px-2 py-2 font-medium">
                {t('ops.dashboard.bySendBack.columns.reasons')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.service} className="border-t border-[var(--color-border)]">
                <td className="px-4 py-2.5 font-medium text-[var(--color-text-primary)]">
                  {OPS_SERVICE_LABELS[row.service]}
                </td>
                <td className="px-2 py-2.5 text-[var(--color-text-secondary)]">
                  {row.sendBackRate === null
                    ? formatOpsMetric(null)
                    : t('ops.dashboard.bySendBack.rateWithCounts', {
                        rate: formatOpsMetric(row.sendBackRate, true),
                        sentBack: row.sentBack,
                        requests: row.requests,
                      })}
                </td>
                <td className="px-2 py-2.5 text-[var(--color-text-secondary)]">
                  {duration(row.avgResumeHours, row.avgResumeBusinessDays)}
                </td>
                <td className="px-2 py-2.5 text-[var(--color-text-secondary)]">
                  {row.closedWithoutResume}
                </td>
                <td className="px-2 py-2.5 text-[var(--color-text-secondary)]">{row.pending}</td>
                <td className="px-2 py-2.5 text-xs text-[var(--color-text-secondary)]">
                  {OPS_SEND_BACK_REASONS.filter((r) => row.reasons[r] > 0)
                    .map(
                      (r) => `${t(`ops.detail.sendBack.reasons.${r}`)} ${String(row.reasons[r])}`,
                    )
                    .join(' / ') || formatOpsMetric(null)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
